/**
 * useViewPreference Hook
 *
 * Per-page list/grid view preference, persisted to localStorage.
 *
 * Storage key (per spec):
 *   wassla:viewMode:<pageKey>:<userId or 'guest'>
 *
 * Backward compatibility: if the per-user key is empty, falls back to the
 * legacy shared key (`wassla_view_<pageKey>`) once on read so existing users
 * don't lose their preference. New writes always go to the new format.
 */

import { useState, useEffect, useCallback } from 'react';

// Default view per page (image-heavy = grid, text-heavy = list)
const DEFAULT_VIEWS = {
  books: 'list',
  shop: 'grid',
  jobs: 'list',
  ladies: 'grid',
  scholarships: 'list',
  search: 'grid',
  default: 'grid',
};

const NEW_KEY_PREFIX = 'wassla:viewMode:';
const LEGACY_KEY_PREFIX = 'wassla_view_';

const buildKey = (pageKey, userId) =>
  `${NEW_KEY_PREFIX}${pageKey}:${userId || 'guest'}`;

const readLegacy = (pageKey) => {
  try {
    const v = localStorage.getItem(`${LEGACY_KEY_PREFIX}${pageKey}`);
    return v === 'list' || v === 'grid' ? v : null;
  } catch {
    return null;
  }
};

export function getStoredViewPreference(pageKey, userId) {
  if (typeof window === 'undefined') {
    return DEFAULT_VIEWS[pageKey] || DEFAULT_VIEWS.default;
  }
  try {
    const fresh = localStorage.getItem(buildKey(pageKey, userId));
    if (fresh === 'list' || fresh === 'grid') return fresh;
    const legacy = readLegacy(pageKey);
    if (legacy) return legacy;
  } catch {
    /* ignore */
  }
  return DEFAULT_VIEWS[pageKey] || DEFAULT_VIEWS.default;
}

export function setStoredViewPreference(pageKey, view, userId) {
  if (typeof window === 'undefined') return;
  if (view !== 'list' && view !== 'grid') return;
  try {
    localStorage.setItem(buildKey(pageKey, userId), view);
  } catch {
    /* ignore */
  }
}

export default function useViewPreference(pageKey = 'default', userId) {
  const [view, setViewState] = useState(() =>
    getStoredViewPreference(pageKey, userId)
  );

  // When the userId changes (login / logout / user switch on the same device),
  // re-resolve the preference from THAT user's bucket. This is what gives us
  // per-user persistence on shared devices.
  useEffect(() => {
    const stored = getStoredViewPreference(pageKey, userId);
    if (stored !== view) setViewState(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, userId]);

  const setView = useCallback(
    (newView) => {
      if (newView !== 'list' && newView !== 'grid') return;
      setViewState(newView);
      setStoredViewPreference(pageKey, newView, userId);
    },
    [pageKey, userId]
  );

  const toggleView = useCallback(() => {
    setView(view === 'list' ? 'grid' : 'list');
  }, [view, setView]);

  return {
    view,
    setView,
    isListView: view === 'list',
    isGridView: view === 'grid',
    toggleView,
  };
}
